import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { OrderSummaryManager, OrderSummaryPageStore } from '@ctct/application-layer';
import {
  BillingStrategy,
  isStripeBillingStrategy,
  PLAN_GROUP_IDS,
  type BillingStrategyQuery,
} from '@ctct/domain-layer';
import type { AccountModificationFlowId } from '@ctct/infrastructure-layer';
import type {
  AddOnsHooks,
  ContactsTierSelectHooks,
  EmailPlansHooks,
  OrderSummaryHooks,
  PaymentMethodsHooks,
  PrepaySelectHooks,
  PromoCodesHooks,
} from '@ctct/state-client-layer';
import {
  useAddOnsFacade,
  useBillingAccount,
  useBillingInfo,
  useEntitlement,
  usePaymentPlanFacade,
  usePlanDetail,
  usePlanGroupFacade,
  useRating,
  useSuspensionInfoFacade,
  useIsLegacyPlanGroup,
  useUpgradePlanPromotion,
} from '@ctct/state-server-layer';

import { usePlanIdForSmsBundleAddOns } from '../../hooks/usePlanIdForSmsBundleAddOns';
import {
  usePricingConfiguratorVisibility,
  type UsePricingConfiguratorVisibilityOptions,
} from '../../hooks/usePricingConfiguratorVisibility';
import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { useIsSingleBillChildAccountState } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<OrderSummaryManager, 'orderSummaryInit'>;
  pageStore: OrderSummaryPageStore;
  hooks: OrderSummaryHooks;
  externalHooks: {
    useSelectedEmailPlanId: EmailPlansHooks['useSelectedEmailPlanId'];
    useContactsTier: ContactsTierSelectHooks['useContactsTier'];
    usePrepay: PrepaySelectHooks['usePrepay'];
    useSelectedAddOns: AddOnsHooks['useSelectedAddOns'];
    useHasPendingExternalRedirect: PaymentMethodsHooks['useHasPendingExternalRedirect'];
    useIsPromoValidationPending: PromoCodesHooks['usePromoCodeValidationPending'];
  };
  flags?: {
    hideEditAddOns?: boolean;
    isCanceled?: boolean;
    isSmsBundle?: boolean;
    isMobile?: boolean;
  };
  billingStrategy: BillingStrategyQuery;
  flowId: AccountModificationFlowId;
  pricingConfiguratorVisibilityOptions?: UsePricingConfiguratorVisibilityOptions;
}

const resolveBillingStrategyEnum = (billingStrategy: BillingStrategyQuery): BillingStrategy =>
  billingStrategy.isPending ? BillingStrategy.InHouse : billingStrategy.data;

export function useOrderSummaryWidget({
  pageStore,
  initializer,
  flags = {},
  hooks,
  externalHooks,
  billingStrategy,
  flowId,
  pricingConfiguratorVisibilityOptions,
}: Props) {
  const { shouldHideOrderSummaryContactsTierLabel } = usePricingConfiguratorVisibility(
    pricingConfiguratorVisibilityOptions
  );
  const selectedEmailPlanId = externalHooks.useSelectedEmailPlanId();
  const contactsTier = externalHooks.useContactsTier();
  const paymentPeriod = externalHooks.usePrepay();
  const selectedAddOns = externalHooks.useSelectedAddOns();
  const hasPendingExternalRedirect = externalHooks.useHasPendingExternalRedirect();
  const isPromoValidationPending = externalHooks.useIsPromoValidationPending();

  const ratingPayload = hooks.useRatingCallPayload();
  const ratingQuery = useRating(ratingPayload);
  const billingAccountQuery = useBillingAccount();
  const { t } = useTranslation();

  const planDetailQuery = usePlanDetail();
  const isMicroEmail = planDetailQuery.data?.isMicroEmail() ?? false;
  const isStripeUser = isStripeBillingStrategy(billingStrategy);
  const resolvedBillingStrategy = resolveBillingStrategyEnum(billingStrategy);
  const { isLegacyPlanGroup = false } = useIsLegacyPlanGroup();
  const shouldFetch3PlPromotion =
    planDetailQuery.data?.shouldFetch3PlPromotion(isStripeUser, isLegacyPlanGroup) ?? false;
  const promotionQuery = useUpgradePlanPromotion(shouldFetch3PlPromotion);
  const microEmail3PlGroupId = shouldFetch3PlPromotion
    ? promotionQuery.data?.getRecommended3PlPlanGroupId() ?? PLAN_GROUP_IDS.V21GBB_PLANGROUP_ID
    : undefined;
  // Pass override so that planGroupQuery returns group 109 data for paid Micro Email users.
  // This ensures getRecommendedPlanId() (used when isCanceled=true) returns a plan from the
  // same group that useAddOnsFacade will query, preventing a group/plan-ID mismatch.
  const planGroupQuery = usePlanGroupFacade(microEmail3PlGroupId);

  const { hideEditAddOns = false, isCanceled = false, isSmsBundle = false, isMobile = false } = flags;

  const planIdForPurchaseAddOns = usePlanIdForSmsBundleAddOns({ selectedEmailPlanId, isSmsBundle });

  const planId = isCanceled
    ? planGroupQuery.data?.getRecommendedPlanId()
    : planIdForPurchaseAddOns ?? planGroupQuery.data?.getCurrentPlanId();
  // For paid Micro Email users, Plans & Pricing shows group 109 plans (not their own group 379).
  // Pass the override so the add-ons planGroupId matches the displayed plan IDs.
  const addOnsQuery = useAddOnsFacade(planId, undefined, microEmail3PlGroupId);
  const billingInfoQuery = useBillingInfo();
  const paymentPlanQuery = usePaymentPlanFacade(isStripeUser);
  const SMSEntitlement = useEntitlement('SMS', 'SEND');
  const suspensionInfoQuery = useSuspensionInfoFacade();
  const { isLocked: isSingleBillChildAccount, isPending: isSingleBillChildAccountPending } =
    useIsSingleBillChildAccountState({ billingStrategy });

  const { status, data, handlers, lifecycle } = useMemo(
    () => ({
      ...initializer.orderSummaryInit({
        pageStore,
        queries: [
          addOnsQuery,
          ratingQuery,
          billingAccountQuery,
          planGroupQuery,
          billingInfoQuery,
          paymentPlanQuery,
          SMSEntitlement,
          suspensionInfoQuery,
        ],
        flags: [hideEditAddOns, isSmsBundle, isMobile],
        t,
        externalStateValues: {
          selectedEmailPlanId,
          contactsTier,
          paymentPeriod,
          selectedAddOns,
          isPromoValidationPending,
          hasPendingExternalRedirect,
        },
        billingStrategy: resolvedBillingStrategy,
        flowId,
        isSingleBillChildAccount: Boolean(isSingleBillChildAccount),
        isSingleBillChildAccountPending: Boolean(isSingleBillChildAccountPending),
        hideContactsTierLabel: shouldHideOrderSummaryContactsTierLabel,
      }),
      hooks,
      externalHooks,
    }),
    [
      SMSEntitlement,
      addOnsQuery,
      billingAccountQuery,
      billingInfoQuery,
      contactsTier,
      hideEditAddOns,
      isSmsBundle,
      isMobile,
      hooks,
      initializer,
      pageStore,
      paymentPeriod,
      paymentPlanQuery,
      planGroupQuery,
      ratingQuery,
      selectedAddOns,
      resolvedBillingStrategy,
      selectedEmailPlanId,
      suspensionInfoQuery,
      t,
      hasPendingExternalRedirect,
      isPromoValidationPending,
      externalHooks,
      flowId,
      isSingleBillChildAccount,
      isSingleBillChildAccountPending,
      microEmail3PlGroupId,
      shouldFetch3PlPromotion,
      promotionQuery,
      shouldHideOrderSummaryContactsTierLabel,
    ]
  );

  useWidgetLifecycle(status, lifecycle);

  return { status, hooks, data, handlers, externalHooks };
}

export type OrderSummaryWidgetProps = ReturnType<typeof useOrderSummaryWidget>;
